import React, { ReactNode } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import { SelectOption, SelectPanelOptionRendererProps } from '../types';
import { getOptionLabel } from '../utils';
import styles from '../SelectPanel.module.scss';

type DefaultOptionProps = {
  option: SelectOption;
  isMulti: boolean;
  isSelected: boolean;
};

function DefaultOption({ option, isMulti, isSelected }: DefaultOptionProps) {
  const { t } = useTranslation();
  return (
    <>
      {isMulti && (
        <span className={styles.optionCheckbox}>
          <MaterialIcon
            icon={isSelected ? 'check_box' : 'check_box_outline_blank'}
            size="md-18"
          />
        </span>
      )}
      <span className={styles.optionLabel}>{getOptionLabel(option, t)}</span>
    </>
  );
}

type SelectPanelOptionProps = {
  debouncedSearch: string;
  index: number;
  isMulti: boolean;
  isSelected: boolean;
  listboxId: string;
  onClick: () => void;
  option: SelectOption;
  renderOption?: (props: SelectPanelOptionRendererProps) => ReactNode;
  disabled?: boolean;
};

export function SelectPanelOption({
  debouncedSearch,
  disabled = false,
  index,
  isMulti,
  isSelected,
  listboxId,
  onClick,
  option,
  renderOption,
}: SelectPanelOptionProps) {
  const renderOptionContent = () => {
    if (renderOption) {
      return renderOption({
        option,
        isMulti,
        isSelected,
        searchQuery: debouncedSearch,
      });
    }
    return (
      <DefaultOption
        option={option}
        isMulti={isMulti}
        isSelected={isSelected}
      />
    );
  };

  return (
    <li>
      <button
        type="button"
        id={`${listboxId}-option-${index}`}
        role="option"
        aria-selected={isSelected}
        aria-disabled={disabled}
        className={classNames(styles.optionButton, {
          [styles.optionSelected]: isSelected,
          [styles.optionWithCheckbox]: isMulti,
        })}
        disabled={disabled}
        onClick={onClick}
      >
        {renderOptionContent()}
      </button>
    </li>
  );
}
